import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowRight } from "lucide-react";

const LINKS = [
  { to: "/", label: "Home", testId: "nav-home-link" },
  { to: "/products", label: "Products", testId: "nav-products-link" },
  { to: "/industries", label: "Industries", testId: "nav-industries-link" },
  { to: "/book-demo", label: "Book a Demo", testId: "nav-book-demo-link" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  return (
    <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-xl border-b border-slate-100">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 sm:h-20 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="group flex items-center" onClick={() => setOpen(false)}>
          <img
            src="/yesai-logo.png"
            alt="YesAI — Your Everyday Smart AI"
            className="h-14 sm:h-16 w-auto group-hover:scale-[1.03] transition-transform duration-300"
          />
        </Link>

        <nav className="hidden md:flex items-center gap-1" data-testid="nav-links">
          {LINKS.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              data-testid={link.testId}
              className={({ isActive }) =>
                `relative px-4 py-2 text-sm font-semibold transition-colors duration-200 ${
                  isActive ? "text-brand-red" : "text-slate-600 hover:text-brand-ink"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {link.label}
                  {isActive && (
                    <motion.span
                      layoutId="nav-underline"
                      className="absolute left-4 right-4 -bottom-0.5 h-0.5 bg-brand-red rounded-full"
                    />
                  )}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <Link
            to="/book-demo"
            data-testid="nav-book-demo-btn"
            className="hidden sm:inline-flex items-center gap-2 bg-brand-red hover:bg-brand-redDark text-white text-sm font-bold px-5 py-2.5 rounded-full transition-colors duration-200 shadow-lg shadow-brand-red/25"
          >
            Book a Demo
            <ArrowRight className="w-4 h-4" />
          </Link>
          <button
            data-testid="mobile-menu-btn"
            onClick={() => setOpen(!open)}
            className="md:hidden w-10 h-10 flex items-center justify-center rounded-full border border-slate-200 text-brand-ink"
            aria-label="Toggle menu"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            key={location.pathname}
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25 }}
            className="md:hidden overflow-hidden bg-white border-b border-slate-200"
            data-testid="mobile-menu"
          >
            <div className="px-5 py-4 flex flex-col gap-1">
              {LINKS.map((link) => (
                <NavLink
                  key={link.to}
                  to={link.to}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-${link.testId}`}
                  className={({ isActive }) =>
                    `px-4 py-3 rounded-xl text-sm font-semibold transition-colors duration-200 ${
                      isActive ? "bg-brand-redSoft text-brand-red" : "text-slate-600 hover:bg-slate-50"
                    }`
                  }
                >
                  {link.label}
                </NavLink>
              ))}
              <Link
                to="/book-demo"
                onClick={() => setOpen(false)}
                data-testid="mobile-nav-book-demo-btn"
                className="mt-2 inline-flex items-center justify-center gap-2 bg-brand-red text-white text-sm font-bold px-5 py-3 rounded-full"
              >
                Book a Demo
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
